import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import UserService from './users.service';

type ActionType = 'updateUser' | 'createNewUser' | 'archiveUser' | 'restoreUser' | 'resetPasswordAdmin';

interface MutationAction {
  actionType: ActionType;
  payload: any;
}

const useUserMutation = (): {
  executeUserAction: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  isUpdating: Record<string, boolean>;
  setIsUpdating: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  executeUserActionMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
} => {
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});

  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const userService = UserService();

  const actionMap: { [key in ActionType]: Function } = {
    updateUser: userService.updateUser,
    createNewUser: userService.createNewUser,
    archiveUser: userService.archiveUser,
    restoreUser: userService.restoreUser,
    resetPasswordAdmin: userService.resetPassword,
  };

  const executeMutation = async ({ actionType, payload }: MutationAction) => {
    const serviceMethod = actionMap[actionType];
    if (!serviceMethod) {
      enqueueSnackbar('Error processing your request', { variant: 'error' });
    } else {
      return serviceMethod(payload);
    }
  };

  const mutation = useMutation<any, unknown, MutationAction>({
    mutationFn: executeMutation,
    onMutate: ({ actionType, payload }) => {
      setIsUpdating((prev) => ({ ...prev, [actionType]: true }));
    },

    onSuccess: (response: any, { actionType }) => {
      const messages: { [key in ActionType]: string } = {
        updateUser: 'User successfully updated',
        createNewUser: 'User successfully created',
        archiveUser: 'User successfully archived',
        restoreUser: 'User successfully restored',
        resetPasswordAdmin: 'Password successfully reset',
      };

      if (response?.data?.canArchive === false) {
        return;
      } else if (response?.response?.data?.error?.message?.includes('The email is already in use')) {
        throw new Error(response?.response?.data?.error?.message || 'Operation failed');
      } else if (response?.status !== 200) {
        throw new Error(response?.AxiosError?.response?.data?.Message || 'Operation failed');
      }

      const successMessage = messages[actionType] || 'Operation successful';
      const errorMessage = `Failed to ${actionType.replace(/([A-Z])/g, ' $1').toLowerCase()}`;
      enqueueSnackbar(successMessage, { variant: 'success' });
      navigate({ to: '/users' });
    },
    onError: (error: any, { actionType }) => {
      let errorMessage = `Failed to ${actionType.replace(/([A-Z])/g, ' $1').toLowerCase()}`;
      if (error?.message?.includes('email')) {
        errorMessage = errorMessage + '-email already in use';
      }
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['all-users'] });
    },
  });

  return {
    executeUserAction: mutation.mutate,
    isUpdating,
    setIsUpdating,
    executeUserActionMutation: mutation,
  };
};

export default useUserMutation;
